import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * previewSvg(6면도 SVG 문자열)을 PNG data URL로 바꾼다 - ExcelJS는 SVG를 직접 못
 * 넣으므로 Image+canvas로 래스터화한다. 브라우저 전용 API(Image, canvas, Blob
 * URL)라 이 파일은 항상 브라우저에서만 돈다(exceljs 워크북 자체도 그렇다).
 */
const svgToPngDataUrl = (svgMarkup, scale = 2) => new Promise((resolve, reject) => {
  try {
    const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = (img.width || 200) * scale;
      canvas.height = (img.height || 150) * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff'; // SVG 배경이 투명이라 흰 배경을 먼저 깔아준다(엑셀 셀 배경에 안 묻히게).
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (evt) => {
      URL.revokeObjectURL(url);
      reject(evt);
    };
    img.src = url;
  } catch (err) {
    reject(err);
  }
});

/**
 * 도면 견적(시범) 결과를 엑셀 한 장으로 받는다.
 * shipmentStatement.js(거래명세표)처럼 양식지를 채우는 게 아니라, 시범 기능이라
 * 그냥 ExcelJS로 시트 하나를 새로 만든다 - 나중에 정식 견적서 양식이 생기면
 * 그 양식 채우기 방식으로 바꾸면 된다.
 */
export const downloadCadQuoteExcel = async ({ form, costInputs, computed }) => {
  const workbook = new ExcelJS.Workbook();
  const partsById = Object.fromEntries((form.parts || []).map((p) => [p.id, p]));
  const wonFmt = '#,##0"원"';

  // 시트 1: 부품별 표(품번/이름, 재질, 수량, 소재비, 가공비, 구매비, 소계) + 합계.
  const partsSheet = workbook.addWorksheet('부품별 견적');
  partsSheet.columns = [
    { header: '품번/이름', key: 'name', width: 24 },
    { header: '재질', key: 'material', width: 12 },
    { header: '수량', key: 'quantity', width: 8 },
    { header: '소재비', key: 'materialCost', width: 14 },
    { header: '가공비', key: 'machiningCost', width: 14 },
    { header: '구매비', key: 'purchaseCost', width: 14 },
    { header: '소계', key: 'subtotal', width: 14 },
  ];
  partsSheet.getRow(1).font = { bold: true };

  computed.lines.forEach((line) => {
    const part = partsById[line.partId];
    if (!part) return;
    const row = partsSheet.addRow({
      name: part.name,
      material: part.purchased ? '-' : part.material,
      quantity: line.quantity,
      materialCost: Math.round(line.materialCost),
      machiningCost: Math.round(line.machiningCost),
      purchaseCost: Math.round(line.purchaseCost),
      subtotal: Math.round(line.subtotal),
    });
    ['materialCost', 'machiningCost', 'purchaseCost', 'subtotal'].forEach((key) => {
      row.getCell(key).numFmt = wonFmt;
    });
  });

  const totalsRow = partsSheet.addRow({
    name: '합계', quantity: '',
    materialCost: Math.round(computed.materialCost),
    machiningCost: Math.round(computed.machiningCost),
    purchaseCost: Math.round(computed.purchaseCost),
    subtotal: Math.round(computed.materialCost + computed.machiningCost + computed.purchaseCost),
  });
  totalsRow.font = { bold: true };
  ['materialCost', 'machiningCost', 'purchaseCost', 'subtotal'].forEach((key) => {
    totalsRow.getCell(key).numFmt = wonFmt;
  });

  // 시트 2: 산출 근거 + 조립비/외주/마진/부가세까지 포함한 전체 합계.
  // 시트 이름에 '/'가 있으면 exceljs(4.4)가 "엑셀 시트명으로 못 쓰는 문자"로 막는다
  // (실제로 엑셀 다운로드 자체가 깨지는 버그였다) - '·'로 바꿔 피한다.
  const summarySheet = workbook.addWorksheet('산출 근거·합계');
  summarySheet.columns = [
    { header: '항목', key: 'label', width: 30 },
    { header: '값', key: 'value', width: 30 },
  ];
  summarySheet.getRow(1).font = { bold: true };

  const addRow = (label, value, isWon) => {
    const row = summarySheet.addRow({ label, value: isWon ? Math.round(Number(value) || 0) : value });
    if (isWon) row.getCell('value').numFmt = wonFmt;
    return row;
  };

  addRow('품번', form.partNo || '(미지정)');
  addRow('구성', form.isAssembly
    ? `조립품(부품 ${form.parts.length}종, 총 ${form.instanceCount}개)`
    // eslint-disable-next-line no-nested-ternary
    : (form.layered ? `레이어로 나눔(${form.parts.length}개)` : '단일 부품'));
  addRow('전체 부피(cm³)', Number(form.volumeCm3 || 0).toFixed(2));
  addRow('전체 bbox(mm)', `${form.bbox.x} x ${form.bbox.y} x ${form.bbox.z}`);
  summarySheet.addRow({});

  addRow('소재비 합계', computed.materialCost, true);
  addRow('가공비 합계', computed.machiningCost, true);
  addRow('구매비 합계', computed.purchaseCost, true);
  addRow('조립비', computed.assemblyLaborCost, true);
  addRow('외주', computed.outsourceTotal, true);
  addRow('마진율', `${Math.round(costInputs.marginRate * 100)}%`);
  addRow('공급가', computed.supplyPrice, true).font = { bold: true };
  addRow('부가세(10%)', computed.vat, true);
  addRow('합계', computed.total, true).font = { bold: true };

  if (costInputs.outsourceItems.length > 0) {
    summarySheet.addRow({});
    summarySheet.addRow({ label: '외주 상세' }).font = { bold: true };
    costInputs.outsourceItems.forEach((item) => {
      addRow(item.name || '(이름 없음)', item.amount, true);
    });
  }

  // 시트 3: 도면(6면도) - 가공하는(구매품 아닌) 부품마다 previewSvg를 PNG로 바꿔
  // 이름과 함께 올린다. 래스터화가 실패해도(오래된 브라우저 등) 엑셀 전체를
  // 막으면 안 되니, 그 부품 이미지만 건너뛰고 콘솔 경고만 남긴다.
  const machinedParts = (form.parts || []).filter((p) => !p.purchased && p.previewSvg);
  if (machinedParts.length > 0) {
    const drawingSheet = workbook.addWorksheet('도면');
    let rowCursor = 1;
    for (const part of machinedParts) {
      const titleCell = drawingSheet.getCell(`A${rowCursor}`);
      titleCell.value = part.name;
      titleCell.font = { bold: true };
      rowCursor += 1;
      try {
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await svgToPngDataUrl(part.previewSvg);
        const imageId = workbook.addImage({ base64: dataUrl, extension: 'png' });
        drawingSheet.addImage(imageId, { tl: { col: 0, row: rowCursor - 1 }, ext: { width: 420, height: 300 } });
        rowCursor += 18; // 이미지가 대략 차지하는 행 수만큼 다음 품번 제목을 내려준다
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[cadQuoteExcel] 6면도 이미지를 엑셀에 넣는 데 실패해 건너뜁니다.', part.name, err);
      }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const fileName = `도면견적_${form.partNo || 'draft'}.xlsx`;
  saveAs(blob, fileName);
};
